import React, { Fragment, useCallback, useState } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { TeamsAppAnalyticsContext } from '@atlaskit/teams-app-internal-analytics';
import { token } from '@atlaskit/tokens';
import { InteractiveHierarchyModal } from '@atlassian/reporting-lines/interactive-hierarchy-modal';

import { ActionButtons } from './components/ActionButtons';
import { Avatar } from './components/Avatar';
import { UserContent } from './components/UserContent';

const NoopComponent = () => null;

const styles = cssMap({
	root: {
		position: 'relative',
		minWidth: '0px',
		borderRadius: token('radius.large'),
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		backgroundColor: token('elevation.surface.sunken'),

		'&:hover': {
			backgroundColor: token('elevation.surface.raised.hovered'),
		},
	},
	rootSurface: {
		backgroundColor: token('elevation.surface'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
	row: {
		minWidth: '0px',
		width: '100%',
	},
	avatarCol: {
		flexShrink: 0,
	},
	actionCol: {
		flexShrink: 0,
	},
});

type UserInlinePreviewData = {
	accountId: string;
	name: string;
	avatarUrl?: string | null;
	jobTitle?: string | null;
	email?: string | null;
	thirdPartyIntegrations?: {
		externalId: string;
		integrationId: string;
		product: string;
	}[];
};

interface UserInlinePreviewProps {
	data: UserInlinePreviewData;
	testId?: string;
	cloudId: string;
	orgId?: string;
	siteId?: string;
	/** Whether to show the org chart button. Defaults to true when orgId is provided. */
	showOrgChartButton?: boolean;
	/** The account ID of the currently logged-in user (viewer). Required to show the profile card on hover in the org chart modal. */
	requestingUserId?: string;
	/**
	 * Optional context describing where this component is rendered. Included as an attribute in
	 * native analytics events so consumers can distinguish e.g. a collaborator click inside a
	 * UserProfilePreview from a standalone UserInlinePreview click. Example: `'collaborator'`
	 */
	renderContext?: string;
	background?: 'sunken' | 'surface';
}

/**
 * Compact inline user preview with avatar, name, job title, and hover-revealed actions.
 *
 * @internal Not ready for consumption. Do not use in production.
 */
export const UserInlinePreview: React.FC<UserInlinePreviewProps & React.RefAttributes<unknown>> =
	componentWithFG('pt_user_inline_preview_enabled', NoopComponent, UserInlinePreviewComponent);

function UserInlinePreviewComponent({
	data,
	testId = 'user-inline-preview',
	cloudId,
	orgId,
	siteId,
	showOrgChartButton = true,
	requestingUserId,
	renderContext,
	background = 'sunken',
}: UserInlinePreviewProps) {
	const { accountId, name, avatarUrl, jobTitle, email, thirdPartyIntegrations } = data;
	const [isHovered, setIsHovered] = useState(false);
	const [isOrgChartModalOpen, setIsOrgChartModalOpen] = useState(false);
	const handleViewOrgChart = useCallback(() => setIsOrgChartModalOpen(true), []);
	const handleOrgChartModalClose = useCallback(() => setIsOrgChartModalOpen(false), []);
	const canShowOrgChart = showOrgChartButton && !!orgId;

	const resolvedSiteId = siteId ?? (cloudId ? `ari:cloud:confluence::site/${cloudId}` : undefined);

	return (
		<Fragment>
			<TeamsAppAnalyticsContext
				data={{ source: 'userInlinePreview', attributes: { accountId, cloudId } }}
			>
				<Box
					xcss={cx(styles.root, background === 'surface' && styles.rootSurface)}
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
					testId={testId}
				>
					<Inline space="space.150" alignBlock="center" xcss={styles.row}>
						<Box xcss={styles.avatarCol}>
							<Avatar src={avatarUrl} name={name} size="medium" />
						</Box>
						<UserContent name={name} jobTitle={jobTitle} />
						<Box xcss={styles.actionCol}>
							<ActionButtons
								name={name}
								accountId={accountId}
								email={email}
								isVisible={isHovered}
								cloudId={cloudId}
								onViewOrgChart={canShowOrgChart ? handleViewOrgChart : undefined}
								thirdPartyIntegrations={thirdPartyIntegrations}
								renderContext={renderContext}
							/>
						</Box>
					</Inline>
				</Box>
			</TeamsAppAnalyticsContext>
			<ModalTransition>
				{isOrgChartModalOpen && canShowOrgChart && orgId && resolvedSiteId && (
					<InteractiveHierarchyModal
						accountId={accountId}
						cloudId={cloudId}
						orgId={orgId}
						siteId={resolvedSiteId}
						onClose={handleOrgChartModalClose}
						requestingUserId={requestingUserId}
					/>
				)}
			</ModalTransition>
		</Fragment>
	);
}
